import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Flex, Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

//components
import ComponentHeader from 'components/componentHeader/componentHeader';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import VerificationModal from 'modules/main/actions/verificationModal';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import NotificationService from 'core/services/notification';
import BoardMembers from '../components/boardMembers';
import MemberForm from '../components/memberForm';
import Icon from 'utils/icon';

// services
import { onAddMemberToBoard, onDeleteMemberFromBoard, onEditMemberFromBoard, onGetMembersList } from '../services/memberServices';
import { onAppointBoard, onGetBoardById } from '../services/boardServices';
import { mapResponseErrors } from 'utils/mapResponseErrors';

//models
import { VerificationPayload } from 'modules/main/actions/commonModels';
import { BoardMember } from '../models/members';
import { Board } from '../models/board';
import { isRTL } from 'core/services/language';

interface BoardAttachMembersPageProps {}

const BoardAttachMembersPage: FunctionComponent<BoardAttachMembersPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [addForm] = Form.useForm();
	const [editForm] = Form.useForm();

	const [board, setBoard] = useState<Board>();

	const [tempEditedMemberId, setTempEditedMemberId] = useState<number | null>(null);
	const [tempDeletedMemberId, setTempDeletedMemberId] = useState<number | null>(null);

	const [addModalOpen, setAddModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState(false);

	const [isLoadingBoard, setIsLoadingBoard] = useState(true);
	const [isLoadingMembers, setIsLoadingMembers] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAppointing, setIsAppointing] = useState(false);

	useEffect(() => {
		getBoardDetails();
	}, []);

	const getBoardDetails = async () => {
		try {
			setIsLoadingBoard(true);

			const response = await onGetBoardById(id!);
			setBoard(response);

			setIsLoadingBoard(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.FETCH_BOARD_DETAILS_ERROR'));
			navigate('/dashboard/actions/boards');
		}
	};

	const loadMembersHandler = async () => {
		try {
			setIsLoadingMembers(true);

			const response = await onGetMembersList(id!, 1);

			setBoard((previous) => ({
				...previous!,
				board_members: response.members,
			}));

			setIsLoadingMembers(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoadingMembers(false);
		}
	};

	const addHandler = async (member: BoardMember, isNewMember: boolean) => {
		try {
			setIsAdding(true);

			const payload = new FormData();
			if (!isNewMember) {
				payload.append('national_id', member.national_id);
				payload.append('position', member.position);
				payload.append('type', member.type);
			} else {
				Object.entries(member).forEach(([key, value]) => {
					payload.append(key, value as any);
				});
			}

			await onAddMemberToBoard(id!, payload);

			loadMembersHandler();
			addForm.resetFields();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_MEMBER_CREATED_SUCCESSFULLY'));

			setIsAdding(false);
			setAddModalOpen(false);
		} catch (error: any) {
			setIsAdding(false);

			if (error.status === 422) {
				mapResponseErrors(error, addForm);
				return;
			}

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const editHandler = async (member: BoardMember, isNewMember: boolean) => {
		try {
			setIsEditing(true);

			const payload = new FormData();
			payload.append('_method', 'PUT');

			if (!isNewMember) {
				payload.append('national_id', member.national_id);
				payload.append('position', member.position);
				payload.append('type', member.type);
			} else {
				Object.entries(member).forEach(([key, value]) => {
					payload.append(key, value as any);
				});
			}

			await onEditMemberFromBoard(id!, tempEditedMemberId!, payload);

			loadMembersHandler();

			editForm.resetFields();
			setIsEditing(false);
			setEditModalOpen(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_MEMBER_UPDATED_SUCCESSFULLY'));
		} catch (error: any) {
			setIsEditing(false);

			if (error.status === 422) {
				mapResponseErrors(error, editForm);
				return;
			}

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const deleteHandler = async () => {
		try {
			setIsDeleting(true);

			await onDeleteMemberFromBoard(board?.id!, tempDeletedMemberId!);

			loadMembersHandler();

			setIsDeleting(false);

			setDeleteModalOpen(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_MEMBER_DELETED_SUCCESSFULLY'));
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onAppoint = async ({ key, method }: VerificationPayload) => {
		try {
			setIsAppointing(true);

			await onAppointBoard(id!, method, key);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_APPOINTED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards');
		} catch (error: any) {
			setIsAppointing(false);

			const errorMessage = error?.response?.data?.data?.governance_errors;
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : (error?.response?.data?.message ?? t('COMMON.SUBMIT_ERROR')));
		}
	};

	return (
		<>
			{isLoadingBoard ? (
				<LoadingComponent />
			) : (
				<Card>
					<ComponentHeader title={t('ACTIONS.BOARD_APPOINTMENT')} subtitle={t('ACTIONS.FILL_TO_APPOINT_BOARD')} />

					<Icon name="registrationStep2" width={280} height={100} style={{ transform: isRTL() ? 'rotateY(180deg)' : 'none' }} />

					<Flex justify="space-between">
						<div>
							<h6 className="mb-0 fw-medium">{t('ACTIONS.BOARD_MEMBERS')}</h6>
							<p>{t('ACTIONS.ADD_BOARD_MEMBERS')}</p>
						</div>

						{board?.status && board.status !== 'active' && (
							<Button type="primary" size="middle" onClick={() => setAddModalOpen(true)}>
								{t('ACTIONS.ADD_MEMBER')}
							</Button>
						)}
					</Flex>

					<BoardMembers
						boardStatus={board?.status!}
						members={board?.board_members || []}
						onEdit={(member) => {
							editForm.setFieldsValue(member);
							setTempEditedMemberId(member.id);
							setEditModalOpen(true);
						}}
						onDelete={(memberId) => {
							setTempDeletedMemberId(memberId);
							setDeleteModalOpen(true);
						}}
						isLoading={isLoadingMembers}
					/>

					{board?.status && board.status !== 'active' && (
						<Flex gap={20} justify="flex-end" className="mt-5">
							<Button type="primary" onClick={() => setVerificationModalOpen(true)}>
								{t('ACTIONS.APPOINT_BOARD')}
							</Button>
						</Flex>
					)}
				</Card>
			)}

			{/* ADD MODAL */}
			<Modal
				open={addModalOpen}
				destroyOnClose
				width={900}
				title={t('ACTIONS.ADD_NEW_MEMBER')}
				onCancel={() => {
					setAddModalOpen(false);
					addForm.resetFields();
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setAddModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<Button key="confirm" type="primary" size="middle" onClick={addForm.submit} loading={isAdding}>
						{t('COMMON.ADD')}
					</Button>,
				]}>
				<MemberForm form={addForm} onSubmit={addHandler} />
			</Modal>

			{/* EDIT MODAL */}
			<Modal
				open={editModalOpen}
				destroyOnClose
				width={900}
				title={t('ACTIONS.EDIT_MEMBER')}
				onCancel={() => {
					setEditModalOpen(false);
					editForm.resetFields();
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setEditModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<Button key="confirm" type="primary" size="middle" onClick={editForm.submit} loading={isEditing}>
						{t('COMMON.UPDATE')}
					</Button>,
				]}>
				<MemberForm form={editForm} onSubmit={editHandler} />
			</Modal>

			{/* DELETE MODAL */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				title={t('ACTIONS.DELETE_MEMBER')}
				subTitle={t('ACTIONS.CONFIRM_DELETE_MEMBER')}
				confirmText={t('COMMON.DELETE')}
				noteType="red"
				note={t('ACTIONS.MEMBER_DELETE_NOTE')}
				confirmColor="red"
				onDiscard={() => setDeleteModalOpen(false)}
				onConfirm={deleteHandler}
				isLoading={isDeleting}
			/>

			{/* VERIFICATION MODAL */}
			<VerificationModal
				open={verificationModalOpen}
				isLoading={isAppointing}
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onAppoint}
				note={t('ACTIONS.BOARD_APPOINTMENT_VERIFICATION_NOTE')}
				noteType="red"
				module="board"
				id={id!}
			/>
		</>
	);
};

export default BoardAttachMembersPage;
